/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import Accordion from "./Accordion";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { Product } from "../types/product";
import PrintLabel from "./PrintLabel";
import PrintRows from "./PrintRows";
import { PrintLabelLayoutProvider } from "./PrintLabelLayoutContext";
import { buildPrintGroups } from "../utils/printGrouping";

interface PrintPreviewProps {
  settings: PrintSettingsStateTypes;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleQuantityChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  lists: Product[];
}

function PrintPreview(props: PrintPreviewProps) {
  const { settings, handleChange, handleQuantityChange, lists } = props;
  const [isPrinting, setIsPrinting] = useState(false);
  const [showPriceAdjustment, setShowPriceAdjustment] = useState(false);
  const [showPriceStrikethrough, setShowPriceStrikethrough] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    onPrintError: () => {
      setIsPrinting(false);
    },
  });

  const handlePrintProcess = () => {
    setIsPrinting(true);

    setTimeout(() => {
      if (printRef.current) {
        reactToPrintFn();
      }
    }, 500);
  };

  const printStyles = {
    fontSize: `${settings?.fontSize}px`,
    paddingTop: `${settings?.marginTop}mm`,
    paddingBottom: `${settings?.marginBottom}mm`,
    paddingLeft: `${settings?.marginLeft}mm`,
    paddingRight: `${settings?.marginRight}mm`,
    height: `${settings?.pageHeight - 0.5}cm`,
    width: `${settings?.pageWidth - 0.5}cm`,
  };

  const previewScale =
    (settings?.printPerLine > 5 && settings?.pageWidth > 18) ||
    settings?.pageWidth > 18
      ? 0.58
      : 1;

  const scaleStyles = () => {
    if (
      (settings?.printPerLine > 5 && settings?.pageWidth > 18) ||
      settings?.pageWidth > 18
    ) {
      return {
        transform: "scale(0.58)",
        transformOrigin: "left top",
      };
    }

    return {
      transform: "scale(1)",
    };
  };

  const pageStyle = `
  @media print {
		@page {
         size: ${settings?.pageWidth}cm ${settings?.pageHeight}cm;
         margin: 0px !important;
        }

		.print-container{
          border: none !important;    
		}
		.parent-container{
		  transform: scale(1) !important;  
		}
	}
`;

  const groups = buildPrintGroups(lists, settings, !isPrinting);

  return (
    <Accordion
      title="Print Preview"
      defaultOpen
      content={
        <div ref={contentRef}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Display Setting</p>
              {Object.entries({
                showDisplay: "Show Data Display",
                showNameDisplay: "Show Name Display",
                showPriceDisplay: "Show Price Display",
                showCodeDisplay: "Show Code Display",
                showDateDisplay: "Show Date Display",
              }).map(([key, label]) => (
                <React.Fragment key={key}>
                  {(key !== "showDisplay" && settings?.showDisplay) ||
                  key === "showDisplay" ? (
                    <div>
                      <label>
                        <input
                          type="checkbox"
                          name={key}
                          checked={(settings as any)[key]}
                          onChange={handleChange}
                        />
                        <span className="ml-2 font-[500]">{label}</span>
                      </label>
                    </div>
                  ) : null}
                </React.Fragment>
              ))}

              {settings?.showDisplay ? (
                <div>
                  <label className="font-[500]">Print Display Quantity</label>
                  <input
                    type="number"
                    min={1}
                    value={settings.displayQuantity}
                    onChange={(e) => handleQuantityChange(e)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-[27%] p-2.5"
                    placeholder="Print Quantity"
                    name="displayQuantity"
                  />
                </div>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <p className="font-semibold">Barcode Attributes</p>
              {Object.entries({
                showName: "Show Name",
                showPrice: "Show Price",
                showCode: "Show Code",
                showDate: "Show Date",
                showBarcode: "Show Barcode",
              }).map(([key, label]) => (
                <div key={key}>
                  <label>
                    <input
                      type="checkbox"
                      name={key}
                      checked={(settings as any)[key]}
                      onChange={handleChange}
                    />
                    <span className="ml-2 font-[500]">{label}</span>
                  </label>
                  </div>
                ))}
              <label>
                <input
                  type="checkbox"
                  checked={showPriceAdjustment}
                  onChange={(event) => setShowPriceAdjustment(event.target.checked)}
                />
                <span className="ml-2 font-[500]">Show Price Adjustment</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={showPriceStrikethrough}
                  onChange={(event) => setShowPriceStrikethrough(event.target.checked)}
                />
                <span className="ml-2 font-[500]">Show Price Strikethrough</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handlePrintProcess}
              disabled={isPrinting}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
            >
              {isPrinting ? "Printing..." : "Print"}
            </button>
          </div>
          <br />
          <div className="w-full overflow-x-auto">
            <div
              style={{
                width: `${(settings?.pageWidth - 0.5) * previewScale}cm`,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <div
                ref={printRef}
                className="parent-container"
                style={scaleStyles()}
              >
                <PrintLabelLayoutProvider>
                  <PrintRows
                    groups={groups}
                    settings={settings}
                    printStyles={printStyles}
                    pageStyle={pageStyle}
                    renderLabel={(label) => (
                      <PrintLabel
                        data={label.product}
                        settings={settings}
                        kind={label.kind}
                        showPriceAdjustment={showPriceAdjustment}
                        showPriceStrikethrough={showPriceStrikethrough}
                      />
                    )}
                  />
                </PrintLabelLayoutProvider>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export default PrintPreview;
